import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { RowData } from "@tanstack/react-table";
import { ArrowUpDownIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RowWithIdentifier } from "../_helpers/row-identifier";
import { getNextSorting } from "../_helpers/sort-menu";
import { DataTableInstance, SortColumn, TimestampSortField } from "../types";

const TIMESTAMP_SORT_FIELD_LABEL_KEYS: Record<TimestampSortField, string> = {
  [TimestampSortField.CreatedAt]: "CreatedAt",
  [TimestampSortField.UpdatedAt]: "UpdatedAt",
  [TimestampSortField.DeletedAt]: "DeletedAt",
};

interface DataTableSortMenuProps<TData extends RowData & RowWithIdentifier> {
  table: DataTableInstance<TData>;
  sortColumns: SortColumn[];
  timestampSortFields: TimestampSortField[];
}

export function DataTableSortMenu<TData extends RowData & RowWithIdentifier>({
  table,
  sortColumns,
  timestampSortFields,
}: DataTableSortMenuProps<TData>) {
  const { t } = useTranslation("common");
  const sorting = table.state.sorting;
  const [activeSort] = sorting;
  const isTimestampSortActive = timestampSortFields.some((field) => field === activeSort?.id);

  const renderSortItem = (sortId: string, label: string) => (
    <DropdownMenuRadioItem
      key={sortId}
      value={sortId}
      className="cursor-pointer"
      onSelect={() => table.setSorting(getNextSorting(sorting, sortId, timestampSortFields))}
    >
      <span className="flex-1">{label}</span>
      {activeSort?.id === sortId &&
        (activeSort.desc ? (
          <ChevronDownIcon role="img" aria-label={t("Descending")} className="text-primary ml-2 h-4 w-4" />
        ) : (
          <ChevronUpIcon role="img" aria-label={t("Ascending")} className="text-primary ml-2 h-4 w-4" />
        ))}
    </DropdownMenuRadioItem>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={isTimestampSortActive ? ButtonVariant.Filled : ButtonVariant.Outlined}
          colorVariant={isTimestampSortActive ? ButtonColorVariant.Primary : ButtonColorVariant.Secondary}
          size={ButtonSize.Icon}
          className="h-9 w-12 shrink-0"
          aria-label={t("SortBy")}
        >
          <ArrowUpDownIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuRadioGroup value={activeSort?.id}>
          {sortColumns.map((column) => renderSortItem(column.id, column.label()))}
          {sortColumns.length > 0 && timestampSortFields.length > 0 && <DropdownMenuSeparator />}
          {timestampSortFields.map((field) => renderSortItem(field, t(TIMESTAMP_SORT_FIELD_LABEL_KEYS[field])))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
